import { ReactNode } from 'react';
import { Control, FieldError, FieldPath, FieldValues } from 'react-hook-form';

import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import useAsyncSearch from '@hooks/useAsyncSearch';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import {
  GetResourceFeatureTemplatesDocument,
  GetResourceFeatureTemplatesQuery,
  GetResourceFeatureTemplatesQueryVariables,
  ResourceFeatureTemplateFragment as ResourceFeatureTemplate
} from '@typing/Generated';

type Props<FormData extends FieldValues> = {
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  initialOption: {
    label: string | undefined;
    value: string | null;
  };
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  required?: boolean;
};

const ResourceFeatureTemplateSelect = <FormData extends FieldValues>({
  control,
  disabled = false,
  error,
  initialOption,
  label,
  name,
  required = false
}: Props<FormData>) => {
  const getResourceFeatureTemplates = useLazyQueryWithPromise<
    GetResourceFeatureTemplatesQuery,
    GetResourceFeatureTemplatesQueryVariables
  >(GetResourceFeatureTemplatesDocument);

  const resourceFeatureTemplateAsyncSearch = useAsyncSearch<ResourceFeatureTemplate>({
    defaultOptions: [],
    getLabel: t => t?.title ?? '',
    getValue: t => t?.id ?? '',
    initialOption
  });

  return (
    <AsyncSelect<FormData>
      control={control}
      defaultOptions={resourceFeatureTemplateAsyncSearch.defaultOptions}
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      onSearch={async search =>
        resourceFeatureTemplateAsyncSearch.searchOptions(
          search,
          getResourceFeatureTemplates({
            fetchPolicy: 'network-only',
            variables: {
              search
            }
          })
        )
      }
      onSelection={(option: Option) => {
        resourceFeatureTemplateAsyncSearch.setCurrentOption(option);
      }}
      required={required}
      value={resourceFeatureTemplateAsyncSearch.currentOption}
    />
  );
};

export default ResourceFeatureTemplateSelect;
